import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Navbar from "../components/layout/Navbar";
import WeekCalendar from "../components/appointments/WeekCalendar";
import PatientCard from "../components/patients/PatientCard";
import PatientDetails from "../components/patients/PatientDetails";
import PatientFormModal from "../components/patients/PatientFormModal";
import FinancialHistoryModal from "../components/patients/FinancialHistoryModal";
import TratamenteModal from "../components/tratamente/TratamenteModal";
import AppointmentsModal from "../components/appointments/AppointmentsModal";
import Button from "../components/ui/Button";
import SearchInput from "../components/ui/SearchInput";
import UserManagement from "../components/users/UserManagement";
import { formatNamePart, sanitizePhone, sanitizeCnp } from "../utils/textUtils";

import { usePatients } from "../context/PatientsContext";
import { useAppointments } from "../context/AppointmentsContext";
import { useUsers } from "../context/UsersContext";
import { useAuth } from "../context/AuthContext";
import { useTratamente } from "../context/TratamenteContext";
import { useWorkingHours } from "../context/WorkingHoursContext";
import WorkingHoursModal from "../components/admin/WorkingHoursModal";
import ServicesCatalogModal from "../components/admin/ServicesCatalogModal";
import PresetsCatalogModal from "../components/admin/PresetsCatalogModal";

export default function AdminPanel() {
  const { currentUser } = useAuth();
  const { canConfigure } = useWorkingHours();

  if (currentUser?.role === "patient") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-600">
        <p className="text-center text-base sm:text-lg">
          Accesul la panoul de administrare este disponibil doar pentru personalul clinicii.
        </p>
      </div>
    );
  }

  const { pacienti = [], deletePatient: deletePatientApi } = usePatients() || {};
  const {
    appointments = [],
    selectAppointmentsByPatient,
    syncPatientDetails,
  } = useAppointments() || {};
  const {
    users = [],
    addUser,
    updateUser,
    deleteUser: deleteUserAccount,
  } = useUsers() || {};
  const {
    tratamente = {},
    ensureLoaded,
    loadingPatients = {},
  } = useTratamente() || {};

  // 🔹 State principal
  const [selectedMedic, setSelectedMedic] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedPatientOverride, setSelectedPatientOverride] = useState(null);
  const [medicSearchTerm, setMedicSearchTerm] = useState("");
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [selectedMedicLabel, setSelectedMedicLabel] = useState("");
  const [selectedPatientLabel, setSelectedPatientLabel] = useState("");
  const [medicDropdownOpen, setMedicDropdownOpen] = useState(false);
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);

  // 🔹 Modale
  const [showAppointments, setShowAppointments] = useState(false);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showFutureAppointments, setShowFutureAppointments] = useState(false);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [showTratamenteModal, setShowTratamenteModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showWorkingHoursModal, setShowWorkingHoursModal] = useState(false);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [showPresetsModal, setShowPresetsModal] = useState(false);

  // 🔹 Funcții utilitare
  const normalizeEmail = (value) => (value || "").trim().toLowerCase();

  const matchPatientByTerm = useCallback((term, pool) => {
    const normalized = (term || "").trim().toLowerCase();
    if (!normalized || !Array.isArray(pool)) return null;
    const matches = pool.filter((p) => {
      const fullName = `${p.lastName || ""} ${p.firstName || ""}`.trim().toLowerCase();
      const email = (p.email || "").toString().toLowerCase();
      const phone = (p.phone || "").toString().toLowerCase();
      const cnp = (p.cnp || "").toString().toLowerCase();
      const code = (p.code || "").toString().toLowerCase();
      return (
        fullName === normalized ||
        email === normalized ||
        phone === normalized ||
        cnp === normalized ||
        code === normalized
      );
    });
    if (matches.length === 1) return matches[0];
    return null;
  }, []);

  const buildAppointmentDateTime = (appointment) => {
    if (!appointment) return null;
    const dateStr = appointment.date || appointment.startDate || "";
    if (!dateStr) return null;

    let timeStr = "00:00";
    if (typeof appointment.time === "string" && appointment.time.trim()) {
      const trimmed = appointment.time.trim();
      timeStr = trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed;
    } else if (typeof appointment.startTime === "string" && appointment.startTime.trim()) {
      const trimmed = appointment.startTime.trim();
      timeStr = trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed;
    } else if (typeof appointment.hour === "number") {
      const hour = Math.floor(appointment.hour);
      const minutes = Math.round((appointment.hour - hour) * 60);
      const minuteStr = String(minutes).padStart(2, "0");
      timeStr = `${String(hour).padStart(2, "0")}:${minuteStr}`;
    }

    const isoCandidate =
      timeStr.length === 5
        ? `${dateStr}T${timeStr}`
        : `${dateStr}T${timeStr}:00`;
    const parsed = new Date(isoCandidate);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    const fallback = new Date(dateStr);
    if (!Number.isNaN(fallback.getTime())) return fallback;

    return null;
  };

  const medici = useMemo(
    () => users.filter((u) => ["doctor", "assistant", "admin"].includes(u.role)),
    [users]
  );

  const patientsOfMedic = useMemo(() => {
    if (!selectedMedic) return [];
    const normalizedSelected = normalizeEmail(selectedMedic);
    const medic = users.find((u) => normalizeEmail(u.email) === normalizedSelected);
    const targetEmails = new Set([normalizedSelected]);
    if (medic?.role === "admin") {
      const legacyAdminEmail = normalizeEmail("admin@gmail.com");
      targetEmails.add(legacyAdminEmail);
      targetEmails.add("");
    }
    return pacienti.filter((p) => targetEmails.has(normalizeEmail(p?.medicEmail)));
  }, [selectedMedic, pacienti, users]);

  const selectedPatient = useMemo(() => {
    if (!selectedPatientId) return null;
    if (selectedPatientOverride?.id === selectedPatientId) {
      return selectedPatientOverride;
    }
    const fromMedic = patientsOfMedic.find((p) => p.id === selectedPatientId);
    return fromMedic || pacienti.find((p) => p.id === selectedPatientId) || null;
  }, [selectedPatientId, selectedPatientOverride, patientsOfMedic, pacienti]);

  const medicOptions = useMemo(
    () =>
      medici.map((medic) => ({
        id: medic.email,
        email: medic.email,
        name: medic.name || medic.email,
        role: medic.role,
        isSelected: normalizeEmail(medic.email) === normalizeEmail(selectedMedic),
      })),
    [medici, selectedMedic]
  );

  const filteredMedici = useMemo(() => {
    const term = medicSearchTerm.trim().toLowerCase();
    if (!term) return medicOptions;
    return medicOptions.filter(
      (medic) =>
        (medic.name || "").toLowerCase().includes(term) ||
        (medic.email || "").toLowerCase().includes(term)
    );
  }, [medicOptions, medicSearchTerm]);

  const patientPool = useMemo(() => {
    return selectedMedic ? patientsOfMedic : pacienti;
  }, [selectedMedic, patientsOfMedic, pacienti]);

  const patientOptions = useMemo(
    () =>
      patientPool.map((p) => ({
        id: p.id || p._id,
        lastName: p.lastName,
        firstName: p.firstName,
        cnp: p.cnp,
        phone: p.phone,
        email: p.email,
        medicEmail: p.medicEmail,
        raw: p,
      })),
    [patientPool]
  );

  const filteredPatients = useMemo(() => {
    const term = patientSearchTerm.trim().toLowerCase();
    if (!term) return patientOptions;
    return patientOptions.filter((p) => {
      const name = `${p.lastName || ""} ${p.firstName || ""}`.toLowerCase();
      return (
        name.includes(term) ||
        (p.cnp || "").toLowerCase().includes(term) ||
        (p.phone || "").toLowerCase().includes(term) ||
        (p.email || "").toLowerCase().includes(term)
      );
    });
  }, [patientOptions, patientSearchTerm]);

  const patientAppointments = useMemo(() => {
    if (!selectedPatientId || !selectAppointmentsByPatient) return [];
    const list = selectAppointmentsByPatient(selectedPatientId) || [];
    return list
      .slice()
      .sort((a, b) => {
        const dateA = buildAppointmentDateTime(a);
        const dateB = buildAppointmentDateTime(b);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA - dateB;
      });
  }, [selectedPatientId, selectAppointmentsByPatient]);

  const userLookup = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      const key = normalizeEmail(user.email);
      if (key) {
        map.set(key, user);
      }
    });
    return map;
  }, [users]);

  const patientTreatments = useMemo(() => {
    if (!selectedPatient?.id) return [];
    const list = tratamente[selectedPatient.id];
    return Array.isArray(list) ? list : [];
  }, [tratamente, selectedPatient]);

  const referringMedic = useMemo(() => {
    if (!selectedPatient) return null;

    let earliest = null;
    patientAppointments.forEach((appointment) => {
      const dateTime = buildAppointmentDateTime(appointment);
      if (!dateTime) return;
      const rawEmail =
        appointment?.createdBy ||
        appointment?.doctorEmail ||
        appointment?.doctorId ||
        "";
      const normalized = normalizeEmail(rawEmail);
      if (!normalized) return;
      if (!earliest || dateTime < earliest.date) {
        earliest = {
          date: dateTime,
          rawEmail,
          normalizedEmail: normalized,
        };
      }
    });

    let normalizedEmail = earliest?.normalizedEmail || normalizeEmail(selectedPatient.medicEmail);
    let rawEmail = earliest?.rawEmail || selectedPatient?.medicEmail || "";

    if (!normalizedEmail) return null;

    const user = userLookup.get(normalizedEmail);
    const displayEmail = user?.email || rawEmail || "";
    const displayName =
      user?.name ||
      selectedPatient?.medicName ||
      displayEmail ||
      "Medic necunoscut";
    const userId = user?.id || selectedPatient?.medicId || "";

    return {
      id: typeof userId === "string" ? userId : "",
      email: displayEmail,
      normalizedEmail,
      name: displayName,
    };
  }, [patientAppointments, selectedPatient, userLookup]);

  const commissionMedics = useMemo(() => {
    const seenKeys = new Set();
    const result = [];

    const registerMedic = (candidate) => {
      if (!candidate || typeof candidate !== "object") return;
      const emailKey = normalizeEmail(candidate.email);
      const idKey =
        typeof candidate.id === "string" && candidate.id
          ? candidate.id.trim().toLowerCase()
          : "";
      const uniqueKey = emailKey || idKey;
      if (!uniqueKey || seenKeys.has(uniqueKey)) return;
      seenKeys.add(uniqueKey);
      result.push({
        id: typeof candidate.id === "string" ? candidate.id : "",
        email: typeof candidate.email === "string" ? candidate.email : "",
        name:
          (typeof candidate.name === "string" && candidate.name.trim()) ||
          (typeof candidate.fullName === "string" && candidate.fullName.trim()) ||
          (typeof candidate.displayName === "string" && candidate.displayName.trim()) ||
          (typeof candidate.email === "string" ? candidate.email : "Medic necunoscut"),
        normalizedEmail: emailKey,
        normalizedId: idKey,
      });
    };

    medici.forEach((medic) =>
      registerMedic({
        id: medic.id,
        email: medic.email,
        name: medic.name,
      })
    );

    registerMedic(referringMedic);

    return result;
  }, [medici, referringMedic]);

  const commissionLoading = Boolean(
    selectedPatient?.id && loadingPatients[selectedPatient.id]
  );

  const normalizePatient = (p) => ({
    ...p,
    lastName: formatNamePart(p.lastName),
    firstName: formatNamePart(p.firstName),
    phone: sanitizePhone(p.phone),
    cnp: sanitizeCnp(p.cnp),
    city: formatNamePart(p.city || ""),
    isMinor: !!p.isMinor,
    legalRepresentative: {
      lastName: formatNamePart(p.legalRepresentative?.lastName || ""),
      firstName: formatNamePart(p.legalRepresentative?.firstName || ""),
      city: formatNamePart(p.legalRepresentative?.city || ""),
      relation: p.legalRepresentative?.relation || "",
    },
  });

  const groupFutureAppointments = useMemo(() => {
    const now = new Date();
    return (appointments || []).filter(
      (a) => new Date(`${a.date}T${a.time}`) >= now
    );
  }, [appointments]);

  useEffect(() => {
    if (!selectedPatient) return;
    setPatientDropdownOpen(false);
    setMedicDropdownOpen(false);
  }, [selectedPatient]);

  useEffect(() => {
    if (!selectedPatientId) return;
    const existsInList =
      patientsOfMedic.some((p) => p.id === selectedPatientId) ||
      pacienti.some((p) => p.id === selectedPatientId);
    if (existsInList) {
      setSelectedPatientOverride(null);
    }
  }, [pacienti, patientsOfMedic, selectedPatientId]);

  useEffect(() => {
    if (selectedPatientId) return;
    if (patientDropdownOpen) return;
    const match = matchPatientByTerm(patientSearchTerm, patientPool);
    if (!match) return;
    const label = `${formatNamePart(match.lastName)} ${formatNamePart(match.firstName)}`.trim();
    setSelectedPatientId(match.id);
    setSelectedPatientLabel(label);
    setPatientSearchTerm(label);
    // no auto-open for patient details
  }, [
    matchPatientByTerm,
    patientDropdownOpen,
    patientPool,
    patientSearchTerm,
    selectedPatientId,
  ]);

  useEffect(() => {
    if (
      !showFinancialModal ||
      !selectedPatientId ||
      typeof ensureLoaded !== "function"
    ) {
      return;
    }
    ensureLoaded(selectedPatientId).catch((error) => {
      console.error("Nu am putut încărca tratamentele pacientului:", error);
    });
  }, [showFinancialModal, selectedPatientId, ensureLoaded]);

  // 🔹 Toggle dropdown-uri
  const handleToggleMedicDropdown = () => {
    setMedicDropdownOpen((prev) => !prev);
    setPatientDropdownOpen(false);
  };

  const handleTogglePatientDropdown = () => {
    setPatientDropdownOpen((prev) => !prev);
    setMedicDropdownOpen(false);
  };

  const handleAppointmentPatientSelect = useCallback(
    (patientInfo) => {
      if (!patientInfo) return;
      const targetId = patientInfo.id;
      if (!targetId) return;

      const contextPatient =
        pacienti.find((p) => p.id === targetId) || patientInfo;

      const lastName = formatNamePart(
        contextPatient.lastName || patientInfo.lastName || ""
      );
      const firstName = formatNamePart(
        contextPatient.firstName || patientInfo.firstName || ""
      );

      let label = `${lastName} ${firstName}`.trim();
      if (!label) {
        label =
          contextPatient.code ||
          patientInfo.code ||
          contextPatient.email ||
          patientInfo.email ||
          "";
      }

      setSelectedPatientId(targetId);
      if (label) {
        setSelectedPatientLabel(label);
        setPatientSearchTerm(label);
      } else {
        setSelectedPatientLabel("");
        setPatientSearchTerm("");
      }
      setPatientDropdownOpen(false);

      const normalize = (value) => (value || "").trim().toLowerCase();
      const targetMedicEmail =
        contextPatient.medicEmail ||
        patientInfo.medicEmail ||
        contextPatient.assignedDoctorEmail ||
        "";

      if (!selectedMedic && targetMedicEmail) {
        const medic = medici.find(
          (m) => normalize(m.email) === normalize(targetMedicEmail)
        );
        const medicLabel = medic?.name || targetMedicEmail;
        setSelectedMedic(targetMedicEmail);
        setSelectedMedicLabel(medicLabel);
        setMedicSearchTerm(medicLabel);
        setMedicDropdownOpen(false);
      }

      // do not open patient details modal automatically; keep sidebar selection only
    },
    [pacienti, medici, selectedMedic]
  );

  // 🔹 Funcții user management
  const handleAddUser = async (payload) => {
    if (typeof addUser !== "function") return null;
    try {
      return await addUser(payload);
    } catch (err) {
      throw err;
    }
  };

  const handleSaveUser = async (payload) => {
    if (typeof updateUser !== "function") return null;
    try {
      return await updateUser(payload);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (typeof deleteUserAccount !== "function") return false;
    try {
      await deleteUserAccount(userId);
      return true;
    } catch (err) {
      throw err;
    }
  };
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />

      <main className="flex-grow pt-24 sm:pt-28 p-4 sm:p-6 max-w-6xl mx-auto w-full">
        {/* selectare medic/pacient */}
        <section className="bg-white mt-8 sm:mt-12 rounded-2xl shadow p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* 🔹 Căutare pacient */}
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-bold mb-3">Caută pacient</h2>
              <SearchInput
                value={patientSearchTerm}
                onChange={(value) => {
                  setPatientSearchTerm(value);
                  if (
                    selectedPatientId &&
                    selectedPatientLabel &&
                    value !== selectedPatientLabel
                  ) {
                    setSelectedPatientId(null);
                    setSelectedPatientLabel("");
                  }
                  setPatientDropdownOpen(true);
                }}
                onClick={handleTogglePatientDropdown} // 👈 click toggling
                placeholder="Nume, email sau telefon"
                results={filteredPatients}
                renderResult={(p) => (
                  <>
                    <span className="font-medium">
                      {formatNamePart(p.lastName)} {formatNamePart(p.firstName)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {` ${p.cnp || ""} • ${p.phone || ""}`}
                    </span>
                  </>
                )}
                onSelect={(p) => {
                  const chosen = p.raw || p;
                  const targetId =
                    p.id ||
                    chosen?.id ||
                    chosen?._id ||
                    (chosen?.raw ? chosen.raw.id || chosen.raw._id : null);
                  if (!targetId) {
                    toast.error("Pacientul selectat nu are un ID valid.");
                    return;
                  }
                  setSelectedPatientId(targetId);
                  setSelectedPatientOverride(chosen);
                  const label = `${formatNamePart(p.lastName)} ${formatNamePart(p.firstName)}`.trim();
                  setSelectedPatientLabel(label);
                  setPatientSearchTerm(label);
                  setPatientDropdownOpen(false);
                  // no auto-open for patient details

                  if (!selectedMedic && p.medicEmail) {
                    const medic = medici.find(
                      (m) => normalizeEmail(m.email) === normalizeEmail(p.medicEmail)
                    );
                    const medicLabel = medic?.name || p.medicEmail;
                    setSelectedMedic(p.medicEmail);
                    setSelectedMedicLabel(medicLabel);
                    setMedicSearchTerm(medicLabel);
                  }
                }}
                onClear={() => {
                  setSelectedPatientId(null);
                  setSelectedPatientOverride(null);
                  setSelectedPatientLabel("");
                  setPatientSearchTerm("");
                  setPatientDropdownOpen(false);
                  // no auto-open for patient details
                }}
                showDropdown={patientDropdownOpen}
                setShowDropdown={setPatientDropdownOpen}
                noResultsLabel="Niciun pacient găsit"
                dropdownClassName="max-h-80"
              />
            </div>

            {/* 🔹 Căutare medic */}
            <div className="flex-1">
              <h2 className="text-lg sm:text-xl font-bold mb-3">Caută medic</h2>
              <SearchInput
                value={medicSearchTerm}
                onChange={(value) => {
                  setMedicSearchTerm(value);
                  if (
                    selectedMedic &&
                    selectedMedicLabel &&
                    value !== selectedMedicLabel
                  ) {
                    setSelectedMedic("");
                    setSelectedMedicLabel("");
                    setSelectedPatientId(null);
                    setSelectedPatientLabel("");
                  }
                  setMedicDropdownOpen(true);
                }}
                onClick={handleToggleMedicDropdown} // 👈 click toggling
                placeholder="Nume sau email medic"
                results={filteredMedici}
                renderResult={(m) => (
                  <>
                    <span
                      className={`font-medium ${
                        m.isSelected ? "text-blue-700" : "text-gray-800"
                      }`}
                    >
                      {m.name}
                    </span>
                    <span className="text-xs text-gray-500">{` ${m.email} • ${m.role}`}</span>
                  </>
                )}
                onSelect={(m) => {
                  setSelectedMedic(m.email);
                  setSelectedPatientId(null);
                  const label = m.name || m.email;
                  setSelectedMedicLabel(label);
                  setMedicSearchTerm(label);
                  setPatientSearchTerm("");
                  setMedicDropdownOpen(false);
                }}
                onClear={() => {
                  setSelectedMedic("");
                  setSelectedMedicLabel("");
                  setMedicSearchTerm("");
                  setSelectedPatientId(null);
                  setSelectedPatientLabel("");
                  setPatientSearchTerm("");
                  setMedicDropdownOpen(false);
                }}
                showDropdown={medicDropdownOpen}
                setShowDropdown={setMedicDropdownOpen}
                noResultsLabel="Niciun medic găsit"
                dropdownClassName="max-h-80"
              />
            </div>
          </div>
        </section>

        {/* 🔹 Card pacient selectat */}
        {selectedPatient && (
          <PatientCard
            currentUser={currentUser}
            patient={selectedPatient}
            appointmentsList={patientAppointments}
            onClose={() => {
              setSelectedPatientId(null);
              setSelectedPatientOverride(null);
              setSelectedPatientLabel("");
              setPatientSearchTerm("");
              setShowPatientDetails(false);
            }}
            onDetails={() => setShowPatientDetails(true)}
            onDelete={async () => {
              if (window.confirm("Ștergi acest pacient?")) {
                await deletePatientApi(selectedPatient.id);
                setSelectedPatientId(null);
              }
            }}
            onAppointments={() => setShowAppointments(true)}
            onPlanTratamente={() => setShowTratamenteModal(true)}
            onFinancial={() => setShowFinancialModal(true)}
          />
        )}

        {/* 🔹 Calendar săptămânal */}
        <div className="bg-white rounded-2xl shadow p-2 sm:p-4 mb-6 overflow-x-auto">
          <WeekCalendar
            onAppointmentPatientSelect={handleAppointmentPatientSelect}
            preselectedPatient={selectedPatient}
          />
        </div>

        {/* 🔹 Butoane principale */}
        <div className="flex flex-wrap sm:flex-nowrap gap-2 mb-6 text-xs sm:text-sm">
          {currentUser?.role === "admin" && (
            <>
              <Button
                size="sm"
                variant="secondary"
                className="whitespace-nowrap"
                onClick={() => setShowUserManagement(true)}
              >
                Gestionează utilizatori
              </Button>
              {canConfigure && (
                <Button
                  size="sm"
                  variant="secondary"
                  className="whitespace-nowrap"
                  onClick={() => setShowWorkingHoursModal(true)}
                >
                  Setează program
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                className="whitespace-nowrap"
                onClick={() => setShowServicesModal(true)}
              >
                Servicii & prețuri
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="whitespace-nowrap"
                onClick={() => setShowPresetsModal(true)}
              >
                Preseturi tratament
              </Button>
            </>
          )}
        </div>
      </main>

      {/* 🔹 Detalii pacient */}
      {showPatientDetails && selectedPatient && (
        <PatientDetails
          patient={selectedPatient}
          currentUser={currentUser}
          onClose={() => setShowPatientDetails(false)}
          onSave={(updated, { silent } = {}) => {
            if (updated) {
              const normalized = normalizePatient(updated);
              syncPatientDetails(normalized);
            }
            if (!silent) setShowPatientDetails(false);
          }}
          onEdit={(patientForEdit) => {
            setEditingPatient(patientForEdit);
            setShowPatientDetails(false);
          }}
        />
      )}

      {/* 🔹 Tratamente */}
      {showTratamenteModal && selectedPatient && (
        <TratamenteModal
          patient={selectedPatient}
          treatmentsOverride={patientTreatments}
          commissionMeta={{
            medics: commissionMedics,
            referrer: referringMedic,
          }}
          commissionLoading={commissionLoading}
          onClose={() => setShowTratamenteModal(false)}
        />
      )}

      {/* 🔹 Programări pacient */}
      {showAppointments && selectedPatient && (
        <AppointmentsModal
          title={`Programările pacientului – ${selectedPatient.lastName} ${selectedPatient.firstName}`}
          appointments={patientAppointments}
          patientNameFallback={`${selectedPatient.lastName} ${selectedPatient.firstName}`}
          onClose={() => setShowAppointments(false)}
        />
      )}

      

      {/* 🔹 Editare pacient */}
      {editingPatient && (
        <PatientFormModal
          patient={editingPatient}
          onClose={() => setEditingPatient(null)}
          onSave={(updated) => {
            if (updated) syncPatientDetails(updated);
            setEditingPatient(null);
          }}
        />
      )}

      {/* 🔹 Istoric financiar */}
      {showFinancialModal && selectedPatient && (
        <FinancialHistoryModal
          patientId={selectedPatient.id}
          records={selectedPatient.financialRecords || []}
          patientName={`${selectedPatient.lastName} ${selectedPatient.firstName}`}
          treatments={patientTreatments}
          commissionMeta={{
            medics: commissionMedics,
            referrer: referringMedic,
          }}
          commissionLoading={commissionLoading}
          onClose={() => setShowFinancialModal(false)}
        />
      )}

      {/* 🔹 Management utilizatori */}
      {currentUser?.role === "admin" && showUserManagement && (
        <UserManagement
          users={users}
          onAddUser={handleAddUser}
          onSaveUser={handleSaveUser}
          onDeleteUser={handleDeleteUser}
          onClose={() => setShowUserManagement(false)}
        />
      )}

      {showWorkingHoursModal && (
        <WorkingHoursModal
          open={showWorkingHoursModal}
          onClose={() => setShowWorkingHoursModal(false)}
        />
      )}

      {showServicesModal && (
        <ServicesCatalogModal
          open={showServicesModal}
          onClose={() => setShowServicesModal(false)}
        />
      )}

      {showPresetsModal && (
        <PresetsCatalogModal
          open={showPresetsModal}
          onClose={() => setShowPresetsModal(false)}
        />
      )}
    </div>
  );
}
